import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Loader2, Paperclip, Send, FileText, Image as ImageIcon, X } from 'lucide-react';
import { messengerApi } from '@/pages/messenger/lib/messengerApi';
import { cn } from '@/lib/utils';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { MobileComposerSheet } from '@/components/mobile/messenger';
import type { ChatMessage } from '@/pages/messenger/types';
import type { Workgroup } from '@/pages/messenger/workgroup-hub/types';

interface Props {
  workgroup: Workgroup;
  currentUserId: string | null;
}

/**
 * Tab Chat del gruppo mobile (Task #1721). Apre la conversazione messenger
 * associata al workgroup (workgroup.conversation_id), mostra le bolle in
 * ordine cronologico. Composer = MobileComposerSheet (bottom sheet condiviso
 * con Feed) per coerenza UX e ARIA cross-tab. La barra in fondo è un
 * affordance "tap to type" che apre il sheet.
 *
 * Polling 5s per allineamento; il realtime socket è gestito a livello globale
 * dalla pagina MobileChat — questo tab non si aggancia per evitare doppi
 * listener su tab-switch.
 */
export default function MobileWorkgroupChat({ workgroup, currentUserId }: Props) {
  const convId = workgroup.conversation_id ?? null;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!convId) { setLoading(false); return; }
    try {
      const res = await messengerApi.getMessages(convId, undefined, 50);
      if (res.success && Array.isArray(res.data)) {
        setMessages([...res.data].reverse());
      }
    } finally {
      setLoading(false);
    }
  }, [convId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!convId) return;
    void messengerApi.markRead(convId);
    const t = window.setInterval(() => { void load(); }, 5000);
    return () => window.clearInterval(t);
  }, [convId, load]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  function resetComposer() {
    setDraft('');
    setPending([]);
  }

  async function send() {
    if (!convId) return;
    const text = draft.trim();
    if (!text && pending.length === 0) return;
    setSending(true);
    try {
      if (pending.length > 0) {
        await messengerApi.sendFiles(convId, pending, text || undefined);
      } else {
        await messengerApi.sendMessage(convId, { content: text });
      }
      resetComposer();
      setComposerOpen(false);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore';
      alert(`Errore invio: ${msg}`);
    } finally {
      setSending(false);
    }
  }

  function onFilesPicked(ev: ChangeEvent<HTMLInputElement>) {
    const files = ev.target.files ? Array.from(ev.target.files) : [];
    if (files.length === 0) return;
    setPending((prev) => [...prev, ...files].slice(0, 10));
    ev.target.value = '';
  }

  if (!convId) {
    return (
      <div className="p-6 text-center text-muted-foreground text-sm">
        Conversazione del gruppo non disponibile.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-2"
        data-testid="mobile-wg-chat-scroller"
      >
        {loading && (
          <div className="text-center py-6 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-12 text-muted-foreground/80 text-sm">
            Ancora nessun messaggio. Tocca la barra in basso per iniziare.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          return (
            <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
              <div
                className={cn(
                  'max-w-[78%] rounded-2xl px-3 py-2 text-sm',
                  mine ? 'text-white rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm',
                )}
                style={mine ? { background: auroraPalette.indigo } : undefined}
              >
                {!mine && (
                  <div className={cn(aurora.type.caption, 'text-muted-foreground mb-0.5')}>
                    {m.sender_first_name} {m.sender_last_name}
                  </div>
                )}
                {m.is_deleted ? (
                  <span className="italic opacity-70">messaggio eliminato</span>
                ) : (
                  <span className="whitespace-pre-wrap break-words">{m.content ?? ''}</span>
                )}
                <div className={cn('text-[10px] mt-0.5 text-right', mine ? 'text-white/70' : 'text-muted-foreground/80')}>
                  {new Date(m.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compose bar: affordance tap-to-type che apre MobileComposerSheet. */}
      <div
        className="sticky bottom-0 z-10 border-t border-border bg-background/95 backdrop-blur-md px-2 py-2"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
      >
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          className="w-full flex items-center gap-2 bg-muted border border-border rounded-full pl-4 pr-1 py-1.5 text-left text-sm text-muted-foreground hover:bg-muted/80 min-h-[44px]"
          data-testid="mobile-wg-chat-open-composer"
          aria-label="Apri composer messaggio"
        >
          <span className="flex-1 truncate">{draft.trim() || 'Messaggio…'}</span>
          <span
            className="h-9 w-9 inline-flex items-center justify-center rounded-full text-white shrink-0"
            style={{ background: auroraPalette.indigo }}
            aria-hidden
          >
            <Send className="w-4 h-4" />
          </span>
        </button>
      </div>

      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onFilesPicked} />

      <MobileComposerSheet
        open={composerOpen}
        onOpenChange={(o) => { if (!o && !sending) { /* keep draft & pending */ } setComposerOpen(o); }}
        title={`Messaggio · ${workgroup.name}`}
        placeholder="Scrivi un messaggio…"
        value={draft}
        onValueChange={setDraft}
        onSubmit={send}
        submitting={sending}
        submitDisabled={!draft.trim() && pending.length === 0}
        onPickAttachment={() => fileInputRef.current?.click()}
        submitLabel="Invia"
        attachments={pending.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pending.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs text-foreground/80">
                {f.type.startsWith('image/') ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                <span className="max-w-[120px] truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => setPending((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label={`Rimuovi ${f.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        toolbar={(
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-xs text-muted-foreground inline-flex items-center gap-1 px-2 py-1"
          >
            <Paperclip className="w-3.5 h-3.5" /> Allegati ({pending.length})
          </button>
        )}
      />
    </div>
  );
}
